import { AxiosError } from '@mint-lib/api';
import { TFunction } from '@mint-lib/i18n';

import { objectKeysToSnakeCase } from '@myn/shared/src/utils';

import { CanonicalAPIError } from '../api/types.js';

export const parseApiError = (error: unknown): unknown => {
  if (!isAxiosError(error)) {
    return error;
  }

  if (isCanonicalAPIError(error.response?.data.error)) {
    return error.response?.data.error;
  }

  return error;
};

export const isAxiosError = (
  error: unknown,
): error is AxiosError<{ error: unknown }> => {
  return (error as AxiosError).name === 'AxiosError';
};

export const isCanonicalAPIError = (
  error: unknown,
): error is CanonicalAPIError =>
  error instanceof Object && 'type' in error && 'message' in error;

type SearchParamsObject = Record<string, unknown>;

export const normalizeSearchParams = (params: SearchParamsObject) => {
  const filteredParams = removeEmptyParams(params);
  const searchParams = objectKeysToSnakeCase(filteredParams);

  return searchParams;
};

export const removeEmptyParams = (obj: SearchParamsObject = {}) => {
  const filteredParams = Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) =>
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== '',
    ),
  );
  return filteredParams;
};

export const getErrorMessage = (
  t: TFunction,
  error: unknown,
  errorsMap: Record<string, string>,
) => {
  const extractedError = parseApiError(error);

  if (typeof extractedError === 'string' && errorsMap[extractedError]) {
    return errorsMap[extractedError];
  }

  if (isCanonicalAPIError(extractedError) && errorsMap[extractedError.type]) {
    return errorsMap[extractedError.type];
  }

  return errorsMap.default ?? t('Something went wrong');
};

const mdmFieldFormatRegExp = /^urn:mint:field:(.*)_v2$/;

const mdmFieldsWhiteList = ['platform', 'channel'] as const;

type AllowedMDMFields = (typeof mdmFieldsWhiteList)[number];

function isAllowedField(value: string): value is AllowedMDMFields {
  return (mdmFieldsWhiteList as readonly string[]).includes(value);
}

export const mdmFieldCodec = {
  decode: (serverValue: string): string => {
    const match = mdmFieldFormatRegExp.exec(serverValue);

    if (!match) {
      return serverValue;
    }

    const decodedValue = match[1];

    if (!isAllowedField(decodedValue)) {
      throw new Error(`Invalid mdm field received: ${serverValue}`);
    }

    return decodedValue;
  },

  encode: <T extends string>(
    clientValue: T,
  ): T extends AllowedMDMFields ? `urn:mint:field:${T}_v2` : T => {
    if (isAllowedField(clientValue)) {
      return `urn:mint:field:${clientValue}_v2` as T extends AllowedMDMFields
        ? `urn:mint:field:${T}_v2`
        : T;
    }

    return clientValue as T extends AllowedMDMFields
      ? `urn:mint:field:${T}_v2`
      : T;
  },
} as const;
